import isMobile from 'is-mobile';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';

import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useWidth } from '@/hooks/useWidth';
import toast from 'react-hot-toast';
import loginBrokenImg from '@/assets/images/login-broken.svg';
import Logo from '@/assets/images/logo512.png';
import { authService, setUserInStore } from '@/services/auth.service';

import Checkbox from '../atoms/Checkbox';
import InputField from '../atoms/InputField';
import PasswordField from '../atoms/PasswordField';
import InfoModal from '../molecules/InfoModal';
import { Button } from '../ui/button';
import CountdownTimer from '@/components/atoms/CountDownTimer';
import { store } from '@/store';
import { clearUser, setUser } from '@/store/slices/auth';
import { apiService } from '@/api/apiConfig';
// import { supabase } from '@/supabase.config';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { useClerkAuth, useUser } from '@/clerk.config';
import { useSetupApiAuth } from '@/hooks/useSetupApiAuth';
import { useAuth } from '@clerk/clerk-react';
import { useDispatch } from 'react-redux';
import { setLoggingIn } from '@/store/slices/auth';
// import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import SuspendedUserIcon from '@/assets/icons/suspend-user.svg';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// form schema
const schema = z.object({
  email: z.string().min(2, 'Email is required').email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  rememberMe: z.boolean().optional().default(false)
});

type FormData = z.infer<typeof schema>;

/**
 * Retrieves the stored login state from localStorage.
 *
 * @returns {Object | null} The stored login state, or null if it doesn't exist or has expired.
 */
const getStoredLoginState = () => {
  const storedState = localStorage.getItem('loginState');
  if (!storedState) return null;

  const state = JSON.parse(storedState);
  const expiryTime = new Date(state.expiryTime);

  if (new Date() > expiryTime) {
    localStorage.removeItem('loginState');
    return null;
  }

  return state;
};

/**
 * Stores the current login state in localStorage.
 *
 * @param {number} attempts - The number of login attempts.
 * @param {boolean} timerActive - Indicates if the timer is active.
 */
const storeLoginState = (attempts: number, timerActive: boolean) => {
  const expiryTime = new Date();
  expiryTime.setMinutes(expiryTime.getMinutes() + 1);

  localStorage.setItem(
    'loginState',
    JSON.stringify({
      attempts,
      timerActive,
      expiryTime: expiryTime.toISOString()
    })
  );
};

/**
 * Extracts and formats date from suspension error message.
 *
 * @param {string} errorMessage - The error message containing the ban date.
 * @returns {string} Formatted date string in "DD MMM YYYY" format.
 */
const formatSuspensionDate = (errorMessage: string): string => {
  // Extract the date string from the error message
  const dateMatch = errorMessage.match(/until (.+?) GMT/);
  if (!dateMatch) return '';

  const dateString = dateMatch[1];
  const date = new Date(dateString);

  // Format the date as "DD MMM YYYY"
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  };

  return date.toLocaleDateString('en-GB', options);
};

const LoginForm = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSuspendUserMessage, setShowSuspendUserMessage] = useState(false);
  const [suspendUserMessage, setSuspendUserMessage] = useState<string>('');
  const [loginAttempts, setLoginAttempts] = useState(() => {
    const stored = getStoredLoginState();
    return stored?.attempts || 0;
  });
  const [isTimerActive, setIsTimerActive] = useState(() => {
    const stored = getStoredLoginState();
    return stored?.timerActive || false;
  });

  const navigate = useNavigate();
  const wasPreviouslyRemembered = localStorage.getItem('rememberMe') === 'true';
  const { signOut } = useClerk();

  const { signIn, isLoaded: signInLoaded, setActive } = useSignIn();
  // const { signOut, getToken } = useAuth();
  const { user } = useUser();
  const { isSignedIn, userId, getToken } = useAuth();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    setError,
    getValues
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: wasPreviouslyRemembered
    }
  });

  const width = useWidth();
  const isMobileView = isMobile() ? true : false;
  console.log('isMobileView', isMobileView);

  useEffect(() => {
    // Show timer UI if login attempts exceeded
    if (isTimerActive) {
      setShowSuccess(true);
    }
  }, [isTimerActive]);

  const remainingTime = useMemo(() => {
    const state = getStoredLoginState();
    if (!state?.expiryTime) return 60;
    return Math.max(Math.floor((new Date(state.expiryTime).getTime() - Date.now()) / 1000), 0);
  }, []);

  const handleTryAgainClick = () => {
    if (!isTimerActive && loginAttempts < 1) setShowSuccess(false);
  };

  const handleSignUpClick = () => {
    navigate('/signup');
  };

  const handleForgotPasswordClick = () => {
    navigate('/forgot-password');
  };

  const formatUTCDate = (date: string) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const displaySuspendedUserMessage = async () => {
    console.log('displaySuspendedUserMessage called');
    setShowSuspendUserMessage(true);
    setSuspendUserMessage(`Your account has been suspended`);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/email/${getValues('email')}`, {
        // headers: {
        //   Authorization: `Bearer ${token}`
        // }
      });

      if (!res.ok) {
        console.log('Failed to fetch ban details, response status:', res.status);
        return;
      }

      const { data } = await res.json();
      console.log('Ban data fetched:', data);

      if (data?.banUntil) {
        setSuspendUserMessage(`Your account has been suspended until ${formatUTCDate(data.banUntil)}`);
      }
    } catch (error) {
      console.log('error displaySuspendedUserMessage', error);
      // Keep the generic "Your account has been suspended" message
    }
  };

  console.log('showSuspendUserMessage', showSuspendUserMessage);

  const onSubmit = async (data: LoginFormData) => {
    if (!signInLoaded || !signIn) {
      toast.error('Authentication not ready');
      return;
    }

    console.log('signIn', signIn);

    dispatch(setLoggingIn(true));

    try {
      dispatch(clearUser());
      // await signOut();

      // Sign in with Clerk
      const signInAttempt = await signIn.create({
        identifier: data.email,
        password: data.password
      });

      console.log('signInAttempt', signInAttempt);

      if (signInAttempt.status !== 'complete') {
        throw new Error('Sign in failed');
      }

      console.log('signInAttempt', signInAttempt);

      let userProfileData: any;

      // Activate the session before navigating
      if (setActive) {
        await setActive({ session: signInAttempt.createdSessionId });

        const token = await getToken();
        console.log('token getToken after setActive', token);

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        userProfileData = (await res.json()).data;
        console.log('userProfileData dd', userProfileData);
      }

      if (data.rememberMe) {
        // Persistent session - survives browser restart
        localStorage.setItem('clerkAuthSession', 'true');
        localStorage.setItem('rememberMe', 'true');
        // Clear any session storage flag
        sessionStorage.removeItem('clerkAuthSession');
      } else {
        // Temporary session - cleared on browser close
        sessionStorage.setItem('clerkAuthSession', 'true');
        // Clear localStorage flags
        localStorage.removeItem('clerkAuthSession');
        localStorage.removeItem('rememberMe');
      }

      dispatch(
        setUser({
          firstName: userProfileData?.firstName,
          lastName: userProfileData?.lastName,
          name: userProfileData?.firstName + ' ' + (userProfileData?.lastName || ''),
          email: userProfileData?.email,
          phoneNumber: userProfileData?.phoneNumber,
          photoURL: userProfileData?.profilePicUrl,
          id: userProfileData?.id,
          fid: userProfileData?.firebaseUid,
          username: userProfileData?.username,
          roles: userProfileData?.roles || [],
          status: userProfileData?.status || 'ACTIVE',
          unregisterDate: userProfileData?.unregisterDate || null,
          hourlyRate: userProfileData?.hourlyRate ?? 0
        })
      );

      toast.success('Logged in successfully!');
      localStorage.removeItem('loginState');

      if (userProfileData?.roles?.includes('ADMIN')) {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }

      // Navigate to dashboard - profile data will be fetched there and admin redirect will be handled
      // navigate('/dashboard');
    } catch (error: any) {
      console.log('errorasd:', error.message);
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      let errMessage;
      if (error.message.includes('wrong-password')) {
        errMessage = 'Invalid password. Please try again';
        setError('password', { message: 'Incorrect password' });
      } else if (error.message.includes('user-not-found')) {
        errMessage = 'Please enter registered username';
        setError('email', { message: errMessage });
      }

      if (newAttempts > 10) {
        setShowSuccess(true);
        setIsTimerActive(true);
        storeLoginState(newAttempts, true);
      } else if (error.message.includes('You have been banned') || error.message.includes('banned')) {
        console.log('Banned user detected, showing suspend message');
        displaySuspendedUserMessage();
        return; // Early return to prevent toast from showing
      } else {
        storeLoginState(newAttempts, false);
      }

      if (!error.message.includes('User is temporarily banned')) {
        toast.error(error.message);
      }
    } finally {
      dispatch(setLoggingIn(false));
    }
  };

  return (
    <div
      className={`pb-10 h-full max-w-[616px] mx-auto laptop-h:h-fit md:pr-7 md:pt-8 border md:border-0 ${showSuccess ? 'flex flex-col justify-center' : ''}`}
    >
      {(!showSuccess || isMobileView) && !showSuspendUserMessage && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`max-w-[450px] mt-7 gap-5 mx-auto flex flex-col ${isMobileView ? 'justify-between' : 'justify-center'} py-10 px-5 md:px-0 h-full laptop-h:h-fit`}
        >
          <div>
            <img className="max-w-[130px] mx-auto md:hidden" src={Logo} alt="batjet-logo" />
            <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px]">Sign In</h1>
            <p className="hidden md:block text-lg text-secondary leading-[22.5px]">
              Sign in to access your personalized dashboard and stay updated with the latest features.
            </p>
            <InputField
              className="my-4 mt-5"
              label={'Email'}
              placeholder="Input here"
              name="email"
              type="email"
              register={register}
              error={errors.email}
            />
            <PasswordField
              className="mb-3"
              label={'Password'}
              // description="Password must be consisting minimum 8 characters"
              placeholder="Enter password..."
              name="password"
              register={register}
              error={errors.password}
            />

            <div className="flex items-center justify-between">
              <Checkbox
                name="rememberMe"
                register={register}
                label={<p className="text-xs font-medium md:text-sm text-secondary leading-[0.9125rem]">Remember me</p>}
              />
              <button onClick={handleForgotPasswordClick} type="button" className="text-xs text-primary font-medium">
                forget your password ?
              </button>
            </div>
          </div>
          <div>
            <Button
              type="submit"
              className="my-4 w-full rounded-[24px] text-base font-semibold h-[44px] md:h-[64px] md:text-[18px]"
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'SIGNING IN...' : 'SIGN IN'}
            </Button>
            <p className="text-sm md:text-lg text-center">
              Don't have an account?{' '}
              <button disabled={isSubmitting} onClick={handleSignUpClick} className="text-primary font-bold">
                Sign up
              </button>
            </p>
          </div>
        </form>
      )}

      {showSuccess && !isMobileView && !showSuspendUserMessage && (
        <>
          <h1 className="mt-7 mb-2 text-xl text-center font-bold uppercase md:text-left md:text-[28px]">Sign In</h1>
          <p className="hidden md:block text-lg text-secondary leading-[22.5px]">
            Sign in to access your personalized dashboard and stay updated with the latest features.
          </p>
          <div className="mt-14 w-full flex flex-col items-center justify-center gap-3">
            <img className="w-[144px] h-[144px]" src={loginBrokenImg} alt="success-img" />
            <p className="font-bold text-2xl">You've failed to log in 3 times</p>
            <p className="text-secondary text-[18px] leading-[22px] text-center mx-20">
              You need to wait before you can try logging in again, if you are unsure, you can reset your password.
            </p>
          </div>
          <Button
            onClick={handleTryAgainClick}
            className="mt-[4.25rem] mb-5 rounded-[24px] text-base font-semibold px-6 h-[64px] text-[18px] w-full"
            variant={isTimerActive ? 'outline' : 'default'}
          >
            {isTimerActive ? (
              <CountdownTimer
                duration={60}
                timeLeft={remainingTime}
                isActive={isTimerActive}
                onActiveChange={setIsTimerActive}
                onComplete={() => {
                  setLoginAttempts(0);
                  setIsTimerActive(false);
                  localStorage.removeItem('loginState');
                }}
              />
            ) : (
              <span>TRY AGAIN</span>
            )}
          </Button>
          <a href="#" className="text-[18px] text-center text-primary font-medium">
            forget your password ?
          </a>
        </>
      )}

      {showSuccess && isMobileView && !showSuspendUserMessage && (
        <InfoModal
          isOpen={showSuccess}
          variant="success"
          title="You've failed to log in 3 times"
          message={
            <p>
              You need to wait before you can try logging in again, if you are unsure, you can{' '}
              <a href="#" className="text-primary">
                reset your password
              </a>
              .
            </p>
          }
          buttonText={
            isTimerActive ? (
              <span className="text-white font-semibold">
                <CountdownTimer
                  duration={60}
                  isActive={isTimerActive}
                  timeLeft={remainingTime}
                  onActiveChange={setIsTimerActive}
                  onComplete={() => {
                    setLoginAttempts(0);
                    setIsTimerActive(false);
                    localStorage.removeItem('loginState');
                  }}
                />
              </span>
            ) : (
              <span>Try Again</span>
            )
          }
          onButtonClick={handleTryAgainClick}
          icon={loginBrokenImg}
        />
      )}

      {showSuspendUserMessage && (
        <InfoModal
          isOpen={showSuspendUserMessage}
          variant="success"
          icon={SuspendedUserIcon}
          title={suspendUserMessage}
          message="If you think you're not making any fraud. please contact support@batjet.com"
          buttonText="Close"
          onButtonClick={() => {
            setShowSuspendUserMessage(false);
            dispatch(setLoggingIn(false));
          }}
        />
      )}
    </div>
  );
};

export default LoginForm;
